/**
 * Typography
 *
 * Master styles for headlines and text.
 * See: https://www.figma.com/file/iLh3LOpdfxU049JzeU10GU/Kittl-UI-Update-2023?node-id=5831%3A18331&t=nsgMdxlBvQIL2mSG-4
 *
 */

import { handleTextOverflow } from '@src/components/utilities';
import { QUERIES } from '@src/queries';
import { themeStyle } from '@src/theming';
import styled, { css, RuleSet } from 'styled-components';

import { fontThemeStyle } from './theme';
import { HeadlineProps, ParagraphProps } from './types';

const withBFontWeight = (fontWeight: number): string => {
  return fontWeight ? `& > b, & > strong { font-weight: ${fontWeight}; }` : '';
};

/**
 * Headline
 * padding: 0px;
 */
const h = css<HeadlineProps>`
  color: ${({ color }): string => color ?? themeStyle.varInkMain};

  padding: 0px;
  margin: ${({ margin }): string | number => margin || '0px'};

  ${({ noTextOverflow }): RuleSet | string =>
    noTextOverflow ? handleTextOverflow : ''};
`;

/**
 * @extends h
 * font-weight: 800;
 * font-size: 36px;
 * line-height: 42px;
 */
export const h1 = css<HeadlineProps>`
  ${h};
  font-weight: ${fontThemeStyle.h1FontWeight};
  font-size: ${fontThemeStyle.h1FontSize};
  line-height: ${fontThemeStyle.h1LineHeight};

  letter-spacing: ${fontThemeStyle.h1LetterSpacing ?? 'unset'};
  ${() => withBFontWeight(fontThemeStyle.h1BFontWeight)};

  // Mobile
  ${QUERIES.largeTabletAndDown} {
    font-size: ${fontThemeStyle.h1FontSizeMobile ?? ''};
    line-height: ${fontThemeStyle.h1LineHeightMobile ?? ''};
    letter-spacing: ${fontThemeStyle.h1LetterSpacingMobile ?? ''};
  }
`;

/**
 * @implements h1
 */
export const H1 = styled.h1<HeadlineProps>`
  ${h1};
`;

/**
 * @extends h
 * font-weight: 800;
 * font-size: 26px;
 * line-height: 34px;
 */
export const h2 = css<HeadlineProps>`
  ${h};
  font-weight: ${fontThemeStyle.h2FontWeight};
  font-size: ${fontThemeStyle.h2FontSize};
  line-height: ${fontThemeStyle.h2LineHeight};

  letter-spacing: ${fontThemeStyle.h2LetterSpacing ?? 'unset'};
  ${() => withBFontWeight(fontThemeStyle.h2BFontWeight)};

  // Mobile
  ${QUERIES.largeTabletAndDown} {
    font-size: ${fontThemeStyle.h2FontSizeMobile ?? ''};
    line-height: ${fontThemeStyle.h2LineHeightMobile ?? ''};
    letter-spacing: ${fontThemeStyle.h2LetterSpacingMobile ?? ''};
  }
`;

/**
 * @implements h2
 */
export const H2 = styled.h2<HeadlineProps>`
  ${h2};
`;

/**
 * @extends h
 * font-weight: 700;
 * font-size: 18px;
 * line-height: 24px;
 */
export const h3 = css<HeadlineProps>`
  ${h};
  font-weight: ${fontThemeStyle.h3FontWeight};
  font-size: ${fontThemeStyle.h3FontSize};
  line-height: ${fontThemeStyle.h3LineHeight};

  letter-spacing: ${fontThemeStyle.h3LetterSpacing ?? 'unset'};
  ${() => withBFontWeight(fontThemeStyle.h3BFontWeight)};

  // Mobile
  ${QUERIES.largeTabletAndDown} {
    font-weight: ${fontThemeStyle.h3FontWeightMobile ?? ''};
    font-size: ${fontThemeStyle.h3FontSizeMobile ?? ''};
    line-height: ${fontThemeStyle.h3LineHeightMobile ?? ''};
    letter-spacing: ${fontThemeStyle.h3LetterSpacingMobile ?? ''};
  }
`;

/**
 * @implements h3
 */
export const H3 = styled.h3<HeadlineProps>`
  ${h3};
`;

/**
 * @extends h
 * font-weight: 700;
 * font-size: 16px;
 * line-height: 20px;
 */
export const h4 = css<HeadlineProps>`
  ${h};
  font-weight: ${fontThemeStyle.h4FontWeight};
  font-size: ${fontThemeStyle.h4FontSize};
  line-height: ${fontThemeStyle.h4LineHeight};

  letter-spacing: ${fontThemeStyle.h4LetterSpacing ?? 'unset'};
  ${() => withBFontWeight(fontThemeStyle.h4BFontWeight)};
`;

/**
 * @implements h4
 */
export const H4 = styled.h4<HeadlineProps>`
  ${h4};
`;

/**
 * @extends h
 * font-weight: 600;
 * font-size: 14px;
 * line-height: 18px;
 */
export const h5 = css<HeadlineProps>`
  ${h};
  font-weight: ${fontThemeStyle.h5FontWeight};
  font-size: ${fontThemeStyle.h5FontSize};
  line-height: ${fontThemeStyle.h5LineHeight};

  letter-spacing: ${fontThemeStyle.h5LetterSpacing ?? 'unset'};
  ${() => withBFontWeight(fontThemeStyle.h5BFontWeight)};
`;

/**
 * @implements h5
 */
export const H5 = styled.h5<HeadlineProps>`
  ${h5};
`;

/**
 * @extends h
 * font-weight: 600;
 * font-size: 12px;
 * line-height: 19px;
 */
export const h6 = css<HeadlineProps>`
  ${h};
  font-weight: ${fontThemeStyle.h6FontWeight};
  font-size: ${fontThemeStyle.h6FontSize};
  line-height: ${fontThemeStyle.h6LineHeight};

  letter-spacing: ${fontThemeStyle.h6LetterSpacing ?? 'unset'};
  ${() => withBFontWeight(fontThemeStyle.h6BFontWeight)};
`;

/**
 * @implements h6
 */
export const H6 = styled.h6<HeadlineProps>`
  ${h6};
`;

/**
 * @extends h6
 * font-weight: 700;
 * letter-spacing: 0.05em;
 * text-transform: uppercase;
 */
export const h6Plus = css<HeadlineProps>`
  ${h6};

  font-weight: ${fontThemeStyle.h6PlusFontWeight};
  font-size: ${fontThemeStyle.h6PlusFontSize};
  line-height: ${fontThemeStyle.h6PlusLineHeight};

  letter-spacing: ${fontThemeStyle.h6PlusLetterSpacing ?? 'unset'};
  text-transform: ${fontThemeStyle.h6PlusTextTransform ?? 'unset'};
  ${() => withBFontWeight(fontThemeStyle.h6PlusBFontWeight)};
`;

/**
 * @implements h6Plus
 */
export const H6Plus = styled.h6<HeadlineProps>`
  ${h6Plus};
`;

/**
 * Parapraph
 * font-weight: 500;
 * > b font-weight: 700;
 * > a { cursor: pointer; text-decoration-line: underline; }
 * > ul { margin: 12px 0; }
 *      > li + li { padding-top: 6px; }
 */
const p = css<ParagraphProps>`
  font-weight: ${themeStyle.fontMedium};
  color: ${({ color }): string => color ?? themeStyle.varInkMain};

  padding: 0px;
  margin: ${({ margin }): string | number => margin || '0px'};
  text-decoration: ${({ textDecoration }): string => textDecoration ?? 'unset'};
  cursor: ${({ cursor }): string => cursor ?? 'unset'};

  & > b {
    font-weight: ${themeStyle.fontBold};
  }

  & > a {
    cursor: pointer;
    color: ${({ color }): string => color ?? themeStyle.varInkMain};
    text-decoration-line: underline;
  }

  ul {
    margin: 12px 0;
    li + li {
      padding-top: 6px;
    }
  }

  ${({ noTextOverflow }): RuleSet | string =>
    noTextOverflow ? handleTextOverflow : ''};
`;

/**
 * @extends p
 * font-size: 16px;
 * line-height: 26px;
 * > ul { padding-left: 24px; }
 */
export const p1 = css<ParagraphProps>`
  ${p};
  font-weight: ${fontThemeStyle.p1FontWeight};
  font-size: ${fontThemeStyle.p1FontSize};
  line-height: ${fontThemeStyle.p1LineHeight};

  letter-spacing: ${fontThemeStyle.p1LetterSpacing ?? 'unset'};
  ${() => withBFontWeight(fontThemeStyle.p1BFontWeight)};

  ul {
    padding-left: 24px;
  }
`;

/**
 * @implements p1
 */
export const P1 = styled.p<ParagraphProps>`
  ${p1};
`;

/**
 * @extends p
 * font-size: 14px;
 * line-height: 22px;
 * > ul { padding-left: 21px; }
 */
export const p2 = css<ParagraphProps>`
  ${p};
  font-weight: ${fontThemeStyle.p2FontWeight};
  font-size: ${fontThemeStyle.p2FontSize};
  line-height: ${fontThemeStyle.p2LineHeight};

  letter-spacing: ${fontThemeStyle.p2LetterSpacing ?? 'unset'};
  ${() => withBFontWeight(fontThemeStyle.p2BFontWeight)};

  ul {
    padding-left: 21px;
  }
`;

/**
 * @implements p2
 */
export const P2 = styled.p<ParagraphProps>`
  ${p2};
`;

/**
 * @extends p
 * font-size: 12px;
 * line-height: 19px;
 * > ul { padding-left: 18px; }
 */
export const p3 = css<ParagraphProps>`
  ${p};
  font-weight: ${fontThemeStyle.p3FontWeight};
  font-size: ${fontThemeStyle.p3FontSize};
  line-height: ${fontThemeStyle.p3LineHeight};

  letter-spacing: ${fontThemeStyle.p3LetterSpacing ?? 'unset'};
  ${() => withBFontWeight(fontThemeStyle.p3BFontWeight)};

  ul {
    padding-left: 18px;
  }
`;

/**
 * @implements p3
 */
export const P3 = styled.p<ParagraphProps>`
  ${p3};
`;

/**
 * @extends p
 * font-size: 11px;
 * line-height: 18px;
 * > ul { padding-left: 16px; }
 */
export const p4 = css<ParagraphProps>`
  ${p};
  font-weight: ${fontThemeStyle.p4FontWeight};
  font-size: ${fontThemeStyle.p4FontSize};
  line-height: ${fontThemeStyle.p4LineHeight};

  letter-spacing: ${fontThemeStyle.p4LetterSpacing ?? 'unset'};
  ${() => withBFontWeight(fontThemeStyle.p4BFontWeight)};

  ul {
    padding-left: 16px;
  }
`;

/**
 * @implements p4
 */
export const P4 = styled.p<ParagraphProps>`
  ${p4};
`;
